import { Link, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { Checkbox, Flag, PlatformName, StatusPill, TagChip } from '@/components/ui';
import { SitePreviewHover } from '@/components/sites/SitePreviewHover';
import { label, SITE_HEALTH, SITE_STATUS, sitePlatform, TIER_LABELS } from '@/utils/labels';
import { siteUrl } from '@/utils/siteUrl';
import type { SiteOverview } from '@/types/api';

export interface SiteSelection {
  readonly chosen: ReadonlySet<string>;
  readonly toggle: (ids: readonly string[], on: boolean) => void;
}

/**
 * A site's state in one pill: where it is in its life while that is not
 * "ready" (being created, failed, suspended), and whether it answers after.
 */
export function SiteState({ site }: { readonly site: SiteOverview }): ReactNode {
  return site.status === 'ready' ? <StatusPill map={SITE_HEALTH} status={site.healthStatus} /> : <StatusPill map={SITE_STATUS} status={site.status} />;
}

/** A test site says so; a working one is the rule and carries no mark. */
export function TierTag({ tier }: { readonly tier: string }): ReactNode {
  return tier === 'staging' ? <span className="tag">{label(TIER_LABELS, tier)}</span> : null;
}

export function SiteDomain({ domain }: { readonly domain: string | null }): ReactNode {
  return domain ? (
    <a
      className="mono link-external site-domain"
      href={siteUrl(domain)}
      target="_blank"
      rel="noreferrer noopener"
      title={domain}
      onClick={(event) => event.stopPropagation()}
    >
      <span className="truncate">{domain}</span>
      <ExternalLink size={12} aria-hidden="true" />
    </a>
  ) : (
    <span className="mono faint">домен не назначен</span>
  );
}

/** The server a site runs on, or the external one it lives on, as a link to the server's page (ADR-0058). */
export function ServerLink({ site, empty = '—' }: { readonly site: Pick<SiteOverview, 'nodeId' | 'nodeName' | 'hostNodeId' | 'hostName'>; readonly empty?: string }): ReactNode {
  const id = site.nodeId ?? site.hostNodeId;
  const name = site.nodeName ?? site.hostName;
  if (!id || !name) return <span className="faint">{empty}</span>;
  return (
    <Link to={`/servers/${id}`} className="link-quiet" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
      {name}
    </Link>
  );
}

/**
 * The site list's rows; a row opens the site's page. With a selection, each
 * row can be ticked for one action on many sites (ADR-0046).
 */
export function SitesTable({
  sites,
  selection,
  showServer = true,
}: {
  readonly sites: readonly SiteOverview[];
  readonly selection?: SiteSelection;
  /** Off on a server's own page, where every row would name the same one. */
  readonly showServer?: boolean;
}): ReactNode {
  const navigate = useNavigate();
  const all = sites.length > 0 && sites.every((site) => selection?.chosen.has(site.id));
  const some = sites.some((site) => selection?.chosen.has(site.id));

  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            {selection ? (
              <th scope="col" className="shrink">
                <Checkbox
                  checked={all}
                  indeterminate={some && !all}
                  onChange={(on) => selection.toggle(sites.map((site) => site.id), on)}
                  label={<span className="visually-hidden">Выбрать все показанные</span>}
                />
              </th>
            ) : null}
            <th scope="col">Сайт</th>
            <th scope="col">Теги</th>
            <th scope="col">Платформа</th>
            <th scope="col">Состояние</th>
            {showServer ? <th scope="col">Сервер</th> : null}
          </tr>
        </thead>
        <tbody>
          {sites.map((site) => {
            const platform = sitePlatform(site);
            return (
              <tr
                key={site.id}
                className="row-link"
                onClick={() => navigate(`/sites/${site.id}`)}
                // The row is the target, but a row is not focusable, so the
                // keyboard gets the same action on the same element.
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') navigate(`/sites/${site.id}`);
                }}
              >
                {selection ? (
                  <td className="shrink" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                    <Checkbox
                      checked={selection.chosen.has(site.id)}
                      onChange={(on) => selection.toggle([site.id], on)}
                      label={<span className="visually-hidden">Выбрать {site.displayName}</span>}
                    />
                  </td>
                ) : null}
                <td>
                  <div className="stack stack-tight">
                    <div className="row-tight">
                      <Flag country={site.country} language={site.language} />
                      <SitePreviewHover siteId={site.id} takenAt={site.previewTakenAt}>
                        <span className="strong">{site.displayName}</span>
                      </SitePreviewHover>
                      <TierTag tier={site.tier} />
                    </div>
                    <SiteDomain domain={site.primaryDomain} />
                  </div>
                </td>
                <td>
                  <div className="row-tight row-wrap">
                    {site.tags.length === 0 ? (
                      <span className="faint">—</span>
                    ) : (
                      site.tags.map((tag) => <TagChip key={tag.id} tag={tag} size="sm" />)
                    )}
                  </div>
                </td>
                <td>
                  <PlatformName platform={platform.key} label={platform.label} />
                </td>
                <td>
                  <SiteState site={site} />
                </td>
                {showServer ? (
                  <td className="muted nowrap">
                    <ServerLink site={site} />
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
